import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../stores';
import Dropdown from '../../components/Dropdown';
import { formatProcessCardUpdatedAt } from '../../utils/formatDate';
import type { RecentCollectCollectionSummary } from '../../services/api/collectDataService';
import {
    canManageDataCollectionSummary,
    deleteCollectDataCollection,
    patchCollectDataCollection,
    withdrawMyCollectContribution,
} from '../../services/api/collectDataService';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService';
import { AddDataCollectionCoOwnerModal, GenericModal, RenameDataCollectionModal } from '../modal';

export type HomeDataCollectRowProps = {
    collection: RecentCollectCollectionSummary;
    ownerLabel: string;
    onCollectionUpdated: () => void;
};

function collectStatusPill(
    t: (k: string, o?: { defaultValue?: string }) => string,
    status: string,
): { label: string; className: string } {
    const s = String(status).toLowerCase();
    if (s === 'draft')
        return { label: t('form.collectWorkspace.collectionBadgeDraft', { defaultValue: 'Draft' }), className: 'home-pill home-pill-new' };
    if (s === 'active')
        return { label: t('form.collectWorkspace.collectionBadgeActive', { defaultValue: 'Active' }), className: 'home-pill home-pill-ongoing' };
    if (s === 'closed')
        return { label: t('form.collectWorkspace.collectionBadgeClosed', { defaultValue: 'Closed' }), className: 'home-pill home-pill-done' };
    return { label: status, className: 'home-pill home-pill-ongoing' };
}

const HomeDataCollectRow: React.FC<HomeDataCollectRowProps> = ({ collection, ownerLabel, onCollectionUpdated }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const accessToken = useAppSelector((s) => s.auth.access_token);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [coOwnerModalOpen, setCoOwnerModalOpen] = useState(false);
    const pill = collectStatusPill(t, collection.status);
    const rawTs = collection.updated_at ?? collection.created_at;
    const updatedLabel = rawTs ? formatProcessCardUpdatedAt(rawTs) : '';
    const isDraft = String(collection.status).toLowerCase() === 'draft';
    const previewRaw =
        (collection.business_objective_preview && String(collection.business_objective_preview).trim()) || '';
    const objectiveOrKind =
        previewRaw ||
        t('views.processView.homeDataCollectKind', { defaultValue: 'Structured responses' });

    const isContributorOnly = Boolean(collection.opened_as_contributor);
    const contributorToken = collection.contributor_invitation_token?.trim();
    const canManage = canManageDataCollectionSummary(collection);

    const goOpen = useCallback(async () => {
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.home.recentOpenDataCollection);
        if (isContributorOnly && contributorToken) {
            navigate(`/r/${contributorToken}`);
            return;
        }
        navigate(`/process/data-collect/${collection.id}`);
    }, [collection.id, contributorToken, isContributorOnly, navigate]);

    const openRename = useCallback(() => {
        if (!accessToken || !canManage) return;
        setRenameModalOpen(true);
    }, [accessToken, canManage]);

    const executeRename = useCallback(
        async (trimmed: string): Promise<boolean> => {
            if (!accessToken || !canManage) return false;
            const res = await patchCollectDataCollection(collection.id, { name: trimmed }, accessToken);
            if (res.status === 200) {
                onCollectionUpdated();
                return true;
            }
            return false;
        },
        [accessToken, canManage, collection.id, onCollectionUpdated],
    );

    const runDelete = useCallback(
        async (id: string) => {
            if (!accessToken || !canManage) return false;
            const res = await deleteCollectDataCollection(id, accessToken);
            if (res.status === 200) {
                onCollectionUpdated();
                return true;
            }
            return false;
        },
        [accessToken, canManage, onCollectionUpdated],
    );

    const runWithdraw = useCallback(
        async (id: string) => {
            if (!accessToken || !isContributorOnly) return false;
            const res = await withdrawMyCollectContribution(id, accessToken);
            if (res.status === 200) {
                onCollectionUpdated();
                return true;
            }
            return false;
        },
        [accessToken, isContributorOnly, onCollectionUpdated],
    );

    const showOwnerRowMenu = canManage;
    const showContributorRowMenu = isContributorOnly && !canManage;

    return (
        <>
            <div
                role="button"
                tabIndex={0}
                className="home-process-row home-process-row--datacollect"
                onClick={() => {
                    void goOpen();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void goOpen();
                    }
                }}
            >
                <div className="home-p-icon" aria-hidden>
                    <svg viewBox="0 0 16 16" fill="none">
                        <path d="M2 4H14M2 8H10M2 12H12" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                        <circle cx="13" cy="12" r="2.2" stroke="currentColor" strokeWidth="1.2" />
                        <path d="M12.2 11.2L13.5 12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    </svg>
                </div>
                <div className="home-p-info">
                    <div className="home-p-name-row">
                        <span className="home-p-name">{collection.name}</span>
                        <span className="home-p-type-flag home-p-type-flag--process">
                            {t('views.processView.homeTypeDataCollect', { defaultValue: 'Data collection' })}
                        </span>
                    </div>
                    <div className="home-p-meta home-p-meta--datacollect">
                        <div className="home-p-meta-main">
                            <span className={canManage ? 'home-owner-me' : 'home-owner-other'}>{ownerLabel}</span>
                            <span className="home-mdot" aria-hidden />
                            <span className="home-dc-objective" title={objectiveOrKind}>
                                {objectiveOrKind}
                            </span>
                            <span className="home-mdot" aria-hidden />
                            <span className={`${pill.className} home-dc-pill-shrink`}>{pill.label}</span>
                        </div>
                        {updatedLabel ? (
                            <span className="home-p-date" title={`${t('views.processByGroupView.lastModifiedDate')}: ${updatedLabel}`}>
                                {updatedLabel}
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className="home-p-menu" onClick={(e) => e.stopPropagation()}>
                    {showOwnerRowMenu ? (
                        <Dropdown
                            dropName=""
                            dropdownType="dropdown dropstart"
                            iconClassName="fa fa-ellipsis-h"
                            buttonDropClassName="btn btn-secondary bg-transparent border-0 p-0"
                            styleButton={{}}
                            addProcessOwner={t('views.processView.addCollectionOwner')}
                            addProcessOwnerOnClick={() => setCoOwnerModalOpen(true)}
                            share={undefined}
                            shareAction={undefined}
                            action3=""
                            theRouteOfAction4=""
                            deleteProcess={() => {}}
                            action1=""
                            theRouteOfAction5=""
                            dataBsTarget=""
                            routeFaq=""
                            edit=""
                            dataBsTargetEdit=""
                            publish=""
                            dataBsTargetPublish=""
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            rename={t('words.rename')}
                            renameAction={() => void openRename()}
                            action7={t('words.delete')}
                            action7OnClick={() => setDeleteModalOpen(true)}
                        />
                    ) : showContributorRowMenu ? (
                        <Dropdown
                            dropName=""
                            dropdownType="dropdown dropstart"
                            iconClassName="fa fa-ellipsis-h"
                            buttonDropClassName="btn btn-secondary bg-transparent border-0 p-0"
                            styleButton={{}}
                            share={undefined}
                            shareAction={undefined}
                            action3=""
                            theRouteOfAction4=""
                            deleteProcess={() => {}}
                            action1=""
                            theRouteOfAction5=""
                            dataBsTarget=""
                            routeFaq=""
                            edit=""
                            dataBsTargetEdit=""
                            publish=""
                            dataBsTargetPublish=""
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            action7={t('form.collectWorkspace.withdrawMyContribution', {
                                defaultValue: 'Remove my contribution',
                            })}
                            action7OnClick={() => setWithdrawModalOpen(true)}
                        />
                    ) : null}
                </div>
                <svg className="home-p-arr" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>
            {showOwnerRowMenu && deleteModalOpen ? (
                <GenericModal
                    title={t('words.delete')}
                    actionButton={t('words.delete')}
                    action={() => runDelete(collection.id)}
                    sentence={
                        isDraft
                            ? t('form.collectWorkspace.deleteDataCollectionConfirm', {
                                  defaultValue: 'Delete this draft?',
                              })
                            : t('form.collectWorkspace.deleteDataCollectionConfirmActive')
                    }
                    processId={collection.id}
                    modalDomId={`famla-dc-delete-${collection.id}`}
                    autoShow
                    onHidden={() => setDeleteModalOpen(false)}
                />
            ) : null}
            {showContributorRowMenu && withdrawModalOpen ? (
                <GenericModal
                    title={t('form.collectWorkspace.withdrawMyContribution', {
                        defaultValue: 'Remove my contribution',
                    })}
                    actionButton={t('form.collectWorkspace.withdrawMyContribution', {
                        defaultValue: 'Remove my contribution',
                    })}
                    action={() => runWithdraw(collection.id)}
                    sentence={t('form.collectWorkspace.withdrawMyContributionConfirm', {
                        defaultValue:
                            'Remove your contribution from this collection? Your responses and messages will be deleted.',
                    })}
                    processId={collection.id}
                    modalDomId={`famla-dc-withdraw-${collection.id}`}
                    autoShow
                    onHidden={() => setWithdrawModalOpen(false)}
                />
            ) : null}
            {canManage && renameModalOpen ? (
                <RenameDataCollectionModal
                    collectionId={collection.id}
                    initialName={collection.name}
                    modalDomId={`famla-dc-rename-home-${collection.id}`}
                    autoShow
                    onHidden={() => setRenameModalOpen(false)}
                    onSave={executeRename}
                />
            ) : null}
            {canManage && coOwnerModalOpen ? (
                <AddDataCollectionCoOwnerModal
                    collectionId={collection.id}
                    modalId={`addDataCollectionCoOwner${collection.id}`}
                    onSuccess={() => onCollectionUpdated()}
                    autoShow
                    onHidden={() => setCoOwnerModalOpen(false)}
                />
            ) : null}
        </>
    );
};

export default HomeDataCollectRow;
